import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";

export default function Home() {
  const [competitions, setCompetitions] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    fetch("/data/competitions.json")
      .then((res) => res.json())
      .then((data) => setCompetitions(data.slice(0, 3))) // show top 3
      .catch((err) => console.error(err));

    fetch("/data/projects.json")
      .then((res) => res.json())
      .then((data) => setProjects(data))
      .catch((err) => console.error(err));
  }, []);

  return (
    <div className="relative">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-blue-100 py-32 px-4 overflow-hidden">
        {/* Background floating shapes */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-blue-200 rounded-full opacity-20 animate-pulse mix-blend-multiply pointer-events-none"></div>
        <div className="absolute bottom-0 right-10 w-[500px] h-[500px] bg-purple-200 rounded-full opacity-15 animate-spin-slow mix-blend-multiply pointer-events-none"></div>

        <motion.h1
          className="text-4xl md:text-6xl font-extrabold text-gray-900 mb-4 text-center bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Hi, I'm Kittipat Srilekarat
        </motion.h1>

        <motion.div
          className="flex justify-center gap-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <Link
            to="/competitions"
            className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:from-blue-600 hover:to-blue-800 transform hover:-translate-y-1 transition-all duration-200"
          >
            🚀 View Competitions
          </Link>
          <Link
            to="/projects"
            className="flex items-center gap-2 bg-white border border-blue-500 text-blue-600 px-6 py-3 rounded-full font-semibold shadow hover:bg-blue-50 transform hover:-translate-y-1 transition-all duration-200"
          >
            💡 View Projects
          </Link>
        </motion.div>

        {/* Optional underline / highlight animation */}
        <motion.div
          className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mx-auto mt-8"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        />
      </section>

      {/* Featured Competitions */}
      <section className="py-20 px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12 relative z-10">
          🚀 Featured Competitions
        </h2>

        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {competitions.map((comp, index) => (
            <motion.div
              key={comp.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.15, duration: 0.5 }}
              className="h-80"
            >
              <Link
                to={`/competitions/${comp.id}`}
                state={{ comp }}
                className="flex flex-col h-full w-full bg-white border rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition transform hover:-translate-y-1 cursor-pointer"
              >
                <div className="relative h-48 w-full flex-shrink-0 z-0">
                  <img
                    src={comp.image}
                    alt={comp.title}
                    className="h-full w-full object-cover"
                  />
                  {comp.awards && comp.awards.length > 0 && (
                    <div className="absolute top-3 right-3 bg-white text-gray-800 px-3 py-1 rounded-full text-sm font-semibold shadow-md z-10">
                      🏆 {comp.awards.length}
                    </div>
                  )}
                </div>
                <div className="p-4 flex-1 flex flex-col justify-center items-center text-center z-10">
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">
                    {comp.title}
                  </h3>
                  <p className="text-gray-500 text-sm">{comp.date}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-20 px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12 relative z-10">
          💡 Featured Projects
        </h2>

        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {projects.slice(0, 3).map((proj, index) => (
            <motion.div
              key={proj.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.15, duration: 0.5 }}
              className="h-80"
            >
              <Link
                to={`/projects/${proj.id}`}
                state={{ proj }}
                className="flex flex-col h-full w-full bg-white border rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition transform hover:-translate-y-1 cursor-pointer"
              >
                {/* Project Image */}
                <div className="relative h-48 w-full flex-shrink-0 z-0">
                  <img
                    src={proj.image}
                    alt={proj.title}
                    className="h-full w-full object-cover"
                  />
                </div>

                {/* Project Text */}
                <div className="p-4 flex-1 flex flex-col justify-center items-center text-center z-10">
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">
                    {proj.title}
                  </h3>
                  <p className="text-gray-500 text-sm">{proj.year}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
